import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Download, FileText, FileSpreadsheet, Sparkles, AlertTriangle,
  Lightbulb, Check, Clock, DollarSign, TrendingUp, TrendingDown, Shield,
  RotateCcw, ArrowUpRight, ArrowDownRight, Minus, Loader2, ClipboardList, BarChart3,
  Briefcase, ChevronDown, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import EmailShareDialog from '../components/EmailShareDialog';
import WorkspaceSubNav, { computeEstimateProgress } from '../components/WorkspaceSubNav';
import OutputComparisonSection from '../components/OutputComparisonSection';
import OutputNarrativeSection from '../components/OutputNarrativeSection';
import LiveKPIRail from '../components/LiveKPIRail';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export default function EstimateOutputPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;
  const [outCcy, setOutCcy] = useState(null);
  const outSym = getCurrencySymbol(outCcy);
  useEffect(() => {
    if (!state?.estimateId) return;
    axios.get(`${API_URL}/api/estimates/${state.estimateId}`, { withCredentials: true })
      .then(r => setOutCcy(r.data?.currency_code || null)).catch(() => {});
  }, [state?.estimateId]);

  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [standardResults, setStandardResults] = useState(null);
  const [activeSection, setActiveSection] = useState('summary');
  const [bizCaseLoading, setBizCaseLoading] = useState(false);
  const [bizCaseMenuOpen, setBizCaseMenuOpen] = useState(false);
  const [portfolioBenchmark, setPortfolioBenchmark] = useState(null);

  // Fetch portfolio blended rate benchmark (excluding the current estimate
  // so the "vs portfolio average" chip compares against peers, not self).
  useEffect(() => {
    if (!state?.estimateId) return;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/insights/blended-rate-benchmark`, {
        params: { exclude_estimate_id: state.estimateId },
        withCredentials: true,
      })
      .then((res) => { if (!cancelled) setPortfolioBenchmark(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [state?.estimateId]);

  const generateQuote = async (format) => {
    setBizCaseLoading(true);
    setBizCaseMenuOpen(false);
    toast.info(`Generating ${format.toUpperCase()} Quote — this may take a moment...`);
    try {
      const res = await axios.post(`${API_URL}/api/export/quote`, {
        estimate_id: state?.estimateId,
        format,
      }, { withCredentials: true, responseType: 'blob' });
      const mimeMap = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      };
      const blob = new Blob([res.data], { type: mimeMap[format] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Case_${(state?.implName || 'Report').replace(/\s/g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Quote ${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error('Failed to generate Quote');
    } finally {
      setBizCaseLoading(false);
    }
  };

  // Redirect if no state
  useEffect(() => {
    if (!state?.results) navigate('/estimator', { replace: true });
  }, [state, navigate]);

  const results = state?.results;

  // Group components by group
  const grouped = useMemo(() => {
    if (!results) return {};
    const g = {};
    (results.component_breakdown || []).forEach(c => {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push(c);
    });
    return g;
  }, [results]);

  // Standard grouped
  const standardGrouped = useMemo(() => {
    if (!standardResults) return {};
    const g = {};
    (standardResults.component_breakdown || []).forEach(c => {
      if (!g[c.group]) g[c.group] = {};
      g[c.group][c.name] = c;
    });
    return g;
  }, [standardResults]);

  // Pinned KPIs — surface effort / cost / weeks / risk in the right
  // rail so reps don't have to scroll back to the summary cards.
  const kpiMetrics = useMemo(() => {
    if (!results) return [];
    const fmt = (n) => n?.toLocaleString(undefined, { maximumFractionDigits: 0 });
    const eff = results.total_effort;
    const cost = results.total_cost;
    const weeks = results.timing_meta?.calendar_span_weeks
      ?? results.timing_meta?.effort_based_weeks;
    const overrun = results.timing_meta?.schedule_overrun_weeks || 0;
    const conf = results.confidence;
    return [
      {
        label: 'Total Effort',
        value: eff != null ? `${fmt(eff)} hrs` : '—',
        hint: results.effort_range
          ? `${fmt(results.effort_range.low)}–${fmt(results.effort_range.high)} range`
          : null,
        accent: 'slate',
        testId: 'effort',
      },
      {
        label: 'Total Cost',
        value: cost != null ? `${outSym}${fmt(cost)}` : '—',
        hint: results.cost_range
          ? `${outSym}${fmt(results.cost_range.low)}–${outSym}${fmt(results.cost_range.high)}`
          : null,
        accent: 'cyan',
        testId: 'cost',
      },
      {
        label: 'Duration',
        value: weeks != null ? `${weeks} wk` : '—',
        hint: results.timing_meta?.break_extension_weeks
          ? `+${results.timing_meta.break_extension_weeks}w break ext.`
          : null,
        accent: 'slate',
        testId: 'duration',
      },
      {
        label: 'Schedule Risk',
        value: overrun > 0 ? `+${overrun} wk overrun` : 'On time',
        hint: overrun > 0 ? 'Past committed go-live' : conf != null ? `${conf.toFixed(0)}% confidence` : null,
        accent: overrun > 0 ? 'rose' : 'emerald',
        testId: 'risk',
      },
    ];
  }, [results]);

  // Volume Summary data
  const volumeSummary = useMemo(() => {
    if (!results || !state) return [];
    const volOverrides = state.volumeOverrides || {};
    const volMetrics = state.volumeMetrics || [];
    const complexity = state.complexity || 'standard';
    const rateKey = `${complexity}_hrs_per_vol`;

    const summaryItems = [];
    (results.component_breakdown || []).forEach(comp => {
      if (!comp.volume_based) return;
      const vo = volOverrides[comp.name];
      if (!vo || !vo.enabled || !vo.entries) return;

      const compMetrics = volMetrics.filter(m => m.parent_component === comp.name);
      const subItems = [];
      let compTotalHrs = 0;

      compMetrics.forEach(m => {
        const vol = vo.entries[m.sub_component] || 0;
        if (vol <= 0) return;
        const rate = m[rateKey] || m.standard_hrs_per_vol || 0;
        const hrs = vol * rate;
        compTotalHrs += hrs;
        subItems.push({
          subComponent: m.sub_component,
          volume: vol,
          rate,
          hours: hrs
        });
      });

      if (subItems.length > 0) {
        summaryItems.push({
          component: comp.name,
          group: comp.group,
          role: comp.role,
          subItems,
          totalBaseHrs: compTotalHrs,
          finalEffort: comp.final_effort,
          cost: comp.cost
        });
      }
    });
    return summaryItems;
  }, [results, state]);

  // Compute standard (baseline) estimate on mount
  useEffect(() => {
    if (!state) return;
    const computeStandard = async () => {
      try {
        const selected = Object.entries(state.selectedComponents || {})
          .filter(([_, qty]) => qty > 0)
          .map(([name, quantity]) => ({ component_name: name, quantity }));

        const res = await axios.post(`${API_URL}/api/calculate`, {
          implementation_type: state.implementationType,
          complexity: state.complexity,
          mode: 'simple',
          selected_components: selected,
          modifiers: {
            governance: 1.0, content_readiness: 1.0, integration_complexity: 1.0,
            org_complexity: 1.0, timeline_pressure: 1.0, client_capability: 1.0, change_readiness: 1.0
          },
          hosting_model: state.hostingModel
          // No rate_card_override — uses defaults
        });
        setStandardResults(res.data);
      } catch {
        // non-critical
      }
    };
    computeStandard();
  }, [state]);

  // Auto-generate narrative on mount
  useEffect(() => {
    if (!state?.results) return;
    generateNarrative();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateNarrative = async () => {
    if (!state?.results) return;
    setNarrativeLoading(true);
    try {
      const selected = Object.entries(state.selectedComponents || {})
        .filter(([_, qty]) => qty > 0)
        .map(([name, quantity]) => ({ name, quantity }));

      const res = await axios.post(`${API_URL}/api/narrative/generate`, {
        estimate_results: state.results,
        implementation_type: state.implementationType,
        complexity: state.complexity,
        organization_name: state.orgName,
        implementation_name: state.implName,
        implementation_description: state.implDescription,
        target_audience: state.targetAudience,
        modifiers: state.modifiers,
        selected_components: selected,
        currency_code: outCcy || 'USD',
        currency_symbol: outSym
      }, { withCredentials: true });

      setNarrative(res.data.narrative);
    } catch {
      toast.error('Failed to generate narrative');
    } finally {
      setNarrativeLoading(false);
    }
  };

  // Export helpers
  const getExportPayload = () => {
    const selected = Object.entries(state.selectedComponents || {})
      .filter(([_, qty]) => qty > 0)
      .map(([name, quantity]) => ({ component_name: name, quantity }));
    return {
      implementation_type: state.implementationType,
      complexity: state.complexity,
      mode: 'simple',
      selected_components: selected,
      modifiers: state.modifiers,
      organization_name: state.orgName,
      implementation_name: state.implName,
      implementation_description: state.implDescription,
      target_audience: state.targetAudience,
      hosting_model: state.hostingModel,
      org_id: state.selectedOrgId || null,
      narrative: narrative || null
    };
  };

  const doExport = async (endpoint, filename, ext) => {
    try {
      const response = await axios.post(`${API_URL}/api/export/${endpoint}`, getExportPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${Date.now()}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Export failed: ${ext.toUpperCase()}`);
    }
  };

  const generatePdfBase64 = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/export/pdf`, getExportPayload(), { responseType: 'blob' });
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(response.data);
      });
    } catch { return null; }
  };

  const doAuditExport = async (format) => {
    if (!standardResults) {
      toast.error('Standard results still loading, please wait...');
      return;
    }
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    try {
      const payload = {
        implementation_type: state.implementationType,
        complexity: state.complexity,
        organization_name: state.orgName,
        implementation_name: state.implName,
        implementation_description: state.implDescription,
        target_audience: state.targetAudience,
        hosting_model: state.hostingModel,
        org_id: state.selectedOrgId || null,
        generated_by_name: user?.name || '',
        generated_by_email: user?.email || '',
        currency_code: outCcy,
        actual_results: results,
        standard_results: standardResults,
        modifiers: state.modifiers || {},
        narrative: narrative || null,
      };
      const response = await axios.post(`${API_URL}/api/export/audit-${format}`, payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_report_${Date.now()}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Audit report downloaded (${ext.toUpperCase()})`);
    } catch (err) {
      toast.error(`Audit export failed: ${ext.toUpperCase()}`);
    }
  };

  if (!results) return null;

  const sections = [
    { id: 'summary', label: 'Summary' },
    { id: 'comparison', label: 'Standard vs Actual' },
    { id: 'components', label: 'Components' },
    ...(volumeSummary.length > 0 ? [{ id: 'volume', label: 'Volume Summary' }] : []),
    { id: 'narrative', label: 'AI Analysis' },
    { id: 'audit', label: 'Output Report' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F2040] text-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-lg font-semibold">{state.implName || 'Estimate Report'}</h1>
                <p className="text-xs text-white/60">{state.orgName} &middot; {state.implementationType?.replace('_', ' ')} &middot; {state.complexity}</p>
              </div>
            </div>
            {/* Export toolbar */}
            <div className="flex items-center gap-2">
              {/* Quote Preview */}
              {state?.estimateId && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/estimator/quote/${state.estimateId}`)}
                    className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white text-xs h-8 px-3 rounded-lg" data-testid="quote-btn">
                    <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Quote
                  </Button>
                  <div className="h-5 w-px bg-white/20" />
                </>
              )}
              <div className="h-5 w-px bg-white/20" />
              <Button size="sm" variant="ghost" onClick={() => doExport('pdf', 'lms_estimate', 'pdf')} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="output-export-pdf"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
              <Button size="sm" variant="ghost" onClick={() => doExport('excel', 'lms_estimate', 'xlsx')} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="output-export-excel"><Download className="w-3.5 h-3.5 mr-1" /> Excel</Button>
              <Button size="sm" variant="ghost" onClick={() => doExport('word', 'lms_estimate', 'docx')} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="output-export-word"><FileText className="w-3.5 h-3.5 mr-1" /> Word</Button>
              <Button size="sm" variant="ghost" onClick={() => doExport('pptx', 'lms_estimate', 'pptx')} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="output-export-pptx"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> PPTX</Button>
              <EmailShareDialog
                onGeneratePdf={generatePdfBase64}
                defaultSubject={`LMS Estimate — ${state.implName || 'Report'}`}
                defaultFilename={`lms_estimate_${Date.now()}.pdf`}
                triggerVariant="ghost"
                triggerClassName="text-white/80 hover:text-white hover:bg-white/10 text-xs h-8"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Workspace context sub-nav */}
      {state?.estimateId && (
        <WorkspaceSubNav
          estimateId={state.estimateId}
          activeTab="overview"
          customerName={state?.orgName}
          projectName={state?.implName}
          progress={computeEstimateProgress({
            results: results,
            has_conditions: !!state?.hasConditions,
          })}
        />
      )}

      {/* Section nav */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6" data-testid="output-section-nav">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeSection === s.id ? 'border-[#0F2040] text-[#0F2040]' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`section-tab-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
        <div className="flex-1 min-w-0">

        {/* ── SUMMARY ────────────────────────────────────────────────── */}
        {activeSection === 'summary' && (
          <div className="space-y-6" data-testid="section-summary">
            {/* KPI Cards */}
            {results.timeline_stretch && (
              <TimelineStretchBanner stretch={results.timeline_stretch} />
            )}
            {results.timeline_stretch && (
              <TimelineStretchComparison sym={outSym}
                stretch={results.timeline_stretch}
                totalCost={results.total_cost}
                totalEffort={results.total_effort}
              />
            )}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Clock className="w-4 h-4" /> Total Effort</div>
                  <div className="text-2xl font-bold text-[#0F2040]" data-testid="kpi-effort">{results.total_effort?.toLocaleString(undefined, {maximumFractionDigits: 0})} hrs</div>
                  <div className="text-xs text-slate-400 mt-1">{results.effort_range?.low?.toLocaleString(undefined, {maximumFractionDigits: 0})} - {results.effort_range?.high?.toLocaleString(undefined, {maximumFractionDigits: 0})} hrs range</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><DollarSign className="w-4 h-4" /> Total Cost</div>
                  <div className="text-2xl font-bold text-[#0F2040]" data-testid="kpi-cost">{outSym}{results.total_cost?.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                  <div className="text-xs text-slate-400 mt-1">{outSym}{results.cost_range?.low?.toLocaleString(undefined, {maximumFractionDigits: 0})} - {outSym}{results.cost_range?.high?.toLocaleString(undefined, {maximumFractionDigits: 0})} range</div>
                </CardContent>
              </Card>
              <Card data-testid="kpi-blended-rate-card">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Gauge className="w-4 h-4" /> Blended Rate</div>
                  <div className="text-2xl font-bold text-[#0F2040]" data-testid="kpi-blended-rate">
                    {results.total_effort > 0
                      ? `${outSym}${(results.total_cost / results.total_effort).toLocaleString(undefined, {maximumFractionDigits: 0})}/hr`
                      : '—'}
                  </div>
                  <BlendedRateBenchmarkChip
                    estimateBlended={results.total_effort > 0 ? results.total_cost / results.total_effort : 0}
                    benchmark={portfolioBenchmark}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Shield className="w-4 h-4" /> Confidence</div>
                  <div className="text-2xl font-bold text-[#0F2040]" data-testid="kpi-confidence">{results.confidence?.toFixed(0)}%</div>
                  <div className="text-xs text-slate-400 mt-1">Estimate reliability</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><TrendingUp className="w-4 h-4" /> Complexity</div>
                  <div className="text-2xl font-bold text-[#0F2040]" data-testid="kpi-complexity">{results.complexity_label}</div>
                  <div className="text-xs text-slate-400 mt-1">Score: {results.complexity_score?.toFixed(0)}/100</div>
                </CardContent>
              </Card>
            </div>

            {/* Effort by Group + Role */}
            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Effort by Group</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(results.effort_by_group || {}).sort((a, b) => b[1] - a[1]).map(([group, hours]) => {
                      const pct = results.total_effort > 0 ? (hours / results.total_effort) * 100 : 0;
                      return (
                        <div key={group}>
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="text-slate-700 font-medium">{group}</span>
                            <span className="text-slate-500">{hours.toLocaleString(undefined, {maximumFractionDigits: 0})} hrs ({pct.toFixed(0)}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0F2040] rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Cost by Role</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(results.cost_by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, cost]) => {
                      const roleName = {"PM":"Project Manager","CS":"Consultant Sr","CF":"Consultant","IN":"Integration","DM":"Data Migration","DE":"Development","IF":"Infrastructure","HC":"Hypercare"}[role] || role;
                      const pct = results.total_cost > 0 ? (cost / results.total_cost) * 100 : 0;
                      return (
                        <div key={role}>
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="text-slate-700 font-medium">{roleName}</span>
                            <span className="text-slate-500">{outSym}{cost.toLocaleString(undefined, {maximumFractionDigits: 0})} ({pct.toFixed(0)}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#14B8A6] rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Risks strip */}
            {results.risks?.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-500" /> Risk Flags</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {results.risks.map((r) => (
                      <Badge key={r.message} variant="outline" title={r.detail || undefined} className={`text-xs px-2.5 py-1 ${r.detail ? 'cursor-help' : ''} ${
                        r.type === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                        r.type === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-50 text-slate-600 border-slate-200'
                      }`}>
                        {r.message}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ── COMPARISON ──────────────────────────────────────────── */}
        {activeSection === 'comparison' && (
          <OutputComparisonSection
            currencySymbol={outSym}
            results={results}
            standardResults={standardResults}
            state={state}
            grouped={grouped}
            standardGrouped={standardGrouped}
          />
        )}

        {/* ── COMPONENTS ──────────────────────────────────────────── */}
        {activeSection === 'components' && (
          <div className="space-y-4" data-testid="section-components">
            {Object.entries(grouped).map(([group, comps]) => {
              const totalBaseHrs = comps.reduce((s, c) => s + (c.base_hours || 0), 0);
              const totalFinalHrs = comps.reduce((s, c) => s + (c.final_effort || 0), 0);
              const totalCost = comps.reduce((s, c) => s + (c.cost || 0), 0);
              return (
                <Card key={group}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{group} <span className="text-slate-400 font-normal">({comps.length} components)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col style={{width:'40%'}} />
                        <col style={{width:'10%'}} />
                        <col style={{width:'8%'}} />
                        <col style={{width:'14%'}} />
                        <col style={{width:'14%'}} />
                        <col style={{width:'14%'}} />
                      </colgroup>
                      <thead>
                        <tr className="border-b text-xs text-slate-500">
                          <th className="text-left py-1.5 font-medium">Component</th>
                          <th className="text-center py-1.5 font-medium">Role</th>
                          <th className="text-center py-1.5 font-medium">Qty</th>
                          <th className="text-right py-1.5 font-medium">Base Hrs</th>
                          <th className="text-right py-1.5 font-medium">Final Hrs</th>
                          <th className="text-right py-1.5 font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comps.map(c => (
                          <tr key={c.name} className="border-b border-slate-50">
                            <td className="py-1.5 text-slate-700">{c.display_name || c.name}</td>
                            <td className="py-1.5 text-center"><Badge variant="outline" className="text-[10px]">{c.role}</Badge></td>
                            <td className="py-1.5 text-center text-slate-600">{c.quantity}</td>
                            <td className="py-1.5 text-right text-slate-500">{c.base_hours}</td>
                            <td className="py-1.5 text-right font-medium text-[#0F2040]">{c.final_effort?.toFixed(0)}</td>
                            <td className="py-1.5 text-right font-medium text-[#0F2040]">{outSym}{c.cost?.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-50 border-t-2 border-slate-200" data-testid={`components-subtotal-${group.replace(/\s/g,'-').toLowerCase()}`}>
                          <td className="py-2 text-xs font-bold text-slate-500 uppercase" colSpan={3}>Subtotal</td>
                          <td className="py-2 text-right font-bold text-slate-600">{totalBaseHrs}</td>
                          <td className="py-2 text-right font-bold text-[#0F2040]">{totalFinalHrs.toFixed(0)}</td>
                          <td className="py-2 text-right font-bold text-[#0F2040]">{outSym}{totalCost.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── VOLUME SUMMARY ───────────────────────────────────────── */}
        {activeSection === 'volume' && volumeSummary.length > 0 && (
          <div className="space-y-4" data-testid="section-volume-summary">
            {/* Overview card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  <CardTitle className="text-base">Volume-Based Effort Breakdown</CardTitle>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {volumeSummary.length} component{volumeSummary.length !== 1 ? 's' : ''} using volume-based estimation &middot; Complexity: <span className="font-medium capitalize">{state?.complexity || 'standard'}</span>
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Volume Components</p>
                    <p className="text-xl font-bold text-[#0F2040]" data-testid="vol-summary-count">{volumeSummary.length}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Volume-Driven Hours</p>
                    <p className="text-xl font-bold text-[#0F2040]" data-testid="vol-summary-hours">{volumeSummary.reduce((s, v) => s + v.totalBaseHrs, 0).toLocaleString(undefined, {maximumFractionDigits: 1})}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-slate-500 mb-0.5">Volume-Driven Cost</p>
                    <p className="text-xl font-bold text-[#0F2040]" data-testid="vol-summary-cost">{outSym}{volumeSummary.reduce((s, v) => s + v.cost, 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Per-component detail cards */}
            {volumeSummary.map(item => (
              <Card key={item.component} data-testid={`vol-card-${item.component.replace(/\s+/g, '-').toLowerCase()}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{item.component} <span className="text-slate-400 font-normal">&middot; {item.group}</span></CardTitle>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-500">Role: <Badge variant="outline" className="text-[10px] ml-0.5">{item.role}</Badge></span>
                      <span className="font-semibold text-[#0F2040]">{item.finalEffort?.toFixed(1)} hrs</span>
                      <span className="font-semibold text-[#0F2040]">{outSym}{item.cost?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm" data-testid={`vol-table-${item.component.replace(/\s+/g, '-').toLowerCase()}`}>
                    <colgroup>
                      <col style={{width:'45%'}} />
                      <col style={{width:'15%'}} />
                      <col style={{width:'20%'}} />
                      <col style={{width:'20%'}} />
                    </colgroup>
                    <thead>
                      <tr className="border-b text-xs text-slate-500">
                        <th className="text-left py-1.5 font-medium">Volume Item</th>
                        <th className="text-right py-1.5 font-medium">Volume</th>
                        <th className="text-right py-1.5 font-medium">Rate (hrs/unit)</th>
                        <th className="text-right py-1.5 font-medium">Calculated Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.subItems.map(sub => (
                        <tr key={sub.subComponent} className="border-b border-slate-50">
                          <td className="py-1.5 text-slate-700">{sub.subComponent}</td>
                          <td className="py-1.5 text-right text-slate-600 font-medium">{sub.volume.toLocaleString()}</td>
                          <td className="py-1.5 text-right text-slate-500">{sub.rate}</td>
                          <td className="py-1.5 text-right font-medium text-[#0F2040]">{sub.hours.toLocaleString(undefined, {maximumFractionDigits: 1})}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 border-t-2 border-slate-200">
                        <td className="py-2 text-xs font-bold text-slate-500 uppercase" colSpan={3}>Base Volume Hours</td>
                        <td className="py-2 text-right font-bold text-[#0F2040]">{item.totalBaseHrs.toLocaleString(undefined, {maximumFractionDigits: 1})}</td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── AI ANALYSIS ─────────────────────────────────────────── */}
        {activeSection === 'narrative' && (
          <OutputNarrativeSection
            narrative={narrative}
            narrativeLoading={narrativeLoading}
            generateNarrative={generateNarrative}
          />
        )}

        {/* ── OUTPUT REPORT ────────────────────────────────────────── */}
        {activeSection === 'audit' && (
          <div className="space-y-6" data-testid="section-audit">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#0F2040]" />
                  Output Report
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Download a comprehensive audit report containing the summary, standard vs actual comparison with variances, modifier adjustments, and component-level breakdown. Includes who generated the estimate and when.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Report preview info */}
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-5 py-3 border-b border-slate-200">
                      <h3 className="text-sm font-semibold text-[#0F2040]">Report Contents</h3>
                    </div>
                    <div className="p-5 space-y-3">
                      {[
                        { label: 'Summary Comparison', desc: 'Total Effort, Total Cost, Confidence, and Complexity — Standard vs Actual with variance' },
                        { label: 'Modifier Adjustments', desc: 'All 7 modifier values compared against standard baseline (1.0) with percentage change' },
                        { label: 'Component-Level Comparison', desc: 'Per-component breakdown by group with subtotals and grand total — hours and cost with variance' },
                        { label: 'Audit Metadata', desc: `Generated by ${user?.name || user?.email || 'Unknown'} on ${new Date().toLocaleDateString()}` },
                      ].map((item, i) => (
                        <div key={item.label} className="flex gap-3" data-testid={`audit-content-${i}`}>
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0F2040] text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</div>
                          <div>
                            <div className="text-sm font-medium text-slate-800">{item.label}</div>
                            <div className="text-xs text-slate-500">{item.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Download buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => doAuditExport('pdf')}
                      disabled={!standardResults}
                      className="flex-1 bg-[#0F2040] hover:bg-[#0F2040]/90 text-white h-12"
                      data-testid="audit-download-pdf"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Audit Report (PDF)
                    </Button>
                    <Button
                      onClick={() => doAuditExport('excel')}
                      disabled={!standardResults}
                      className="flex-1 bg-[#14B8A6] hover:bg-[#14B8A6]/90 text-white h-12"
                      data-testid="audit-download-excel"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Download Audit Report (Excel)
                    </Button>
                  </div>

                  {!standardResults && (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-4 py-2.5 rounded-lg border border-amber-200">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Computing standard baseline for comparison...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
        <LiveKPIRail
          storageKey="output"
          title="Live KPIs"
          sourceLabel={state?.implName || state?.orgName || 'Estimate'}
          metrics={kpiMetrics}
        />
      </div>
    </div>
  );
}



/**
 * Small contextual chip rendered beneath the Blended Rate KPI value.
 * Compares this estimate's blended rate to the user's portfolio average
 * (excluding self) and surfaces the delta with a colour-coded indicator.
 *
 * Hidden when the benchmark is still loading, the sample size is too
 * small to be meaningful (< 3 peer estimates), or the current estimate
 * has zero effort.
 */
function BlendedRateBenchmarkChip({ estimateBlended, benchmark }) {
  if (!benchmark || !benchmark.portfolio_blended_rate || benchmark.sample_size < 3 || !estimateBlended) {
    return <div className="text-xs text-slate-400 mt-1">Avg cost per billable hour</div>;
  }
  const portfolio = benchmark.portfolio_blended_rate;
  const deltaPct = ((estimateBlended - portfolio) / portfolio) * 100;
  const sign = deltaPct > 0 ? '↑' : deltaPct < 0 ? '↓' : '·';
  const direction = deltaPct > 0.5 ? 'above' : deltaPct < -0.5 ? 'below' : 'on par with';
  const tone = deltaPct > 0.5
    ? 'text-rose-600'
    : deltaPct < -0.5
      ? 'text-emerald-600'
      : 'text-slate-500';
  const portfolioRounded = Math.round(portfolio).toLocaleString();
  const absPct = Math.abs(deltaPct).toFixed(1);
  return (
    <div className="text-xs mt-1 flex items-center gap-1.5" data-testid="kpi-blended-rate-benchmark">
      <span className={`font-semibold ${tone}`}>{sign} {absPct}%</span>
      <span className="text-slate-400">
        {direction} portfolio avg ${portfolioRounded}/hr
        <span className="text-slate-300"> · n={benchmark.sample_size}</span>
      </span>
    </div>
  );
}


/**
 * Surfaces a single-line callout above the KPI grid when the user's
 * committed Planner timeline diverges from the auto-projected timeline.
 * Tone: amber (sub-linear duration tax = this is a conservative buffer,
 * not an alarm). Only renders when the backend computed a stretch tax.
 */
function TimelineStretchBanner({ stretch }) {
  if (!stretch || !stretch.adjustment_pct) return null;
  const stretching = stretch.planner_weeks > stretch.projected_weeks;
  return (
    <div
      className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border border-amber-200 bg-amber-50/60"
      data-testid="timeline-stretch-banner"
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="text-xs text-[#5C4400] leading-snug">
        <span className="font-semibold">Planner timeline ({stretch.planner_weeks} wk)</span>
        {' '}is {stretch.deviation_pct.toFixed(1)}%{' '}
        {stretching ? 'longer' : 'shorter'} than the auto-projected timeline ({stretch.projected_weeks} wk) — added{' '}
        <span className="font-bold text-amber-700">+{stretch.adjustment_pct.toFixed(1)}%</span>{' '}
        to {stretch.affected_roles?.join(' / ') || 'PM/CS/CF'} for ambient coordination overhead.
        {' '}<span className="text-slate-500">Disable in Project Factors for fixed-fee deals.</span>
      </div>
    </div>
  );
}


/**
 * Side-by-side "Auto-projected vs. Planner-committed" comparison rendered
 * directly under `TimelineStretchBanner`. Lets stakeholders see *exactly*
 * how much the chosen timeline costs versus the math's natural answer —
 * helpful both as transparency for the customer and as a soft nudge when
 * sales is stretching beyond the auto-projection.
 *
 * Auto-projected = current totals MINUS the timeline-stretch tax delta.
 * Planner-committed = current totals (which already include the tax).
 */
function TimelineStretchComparison({ stretch, totalCost, totalEffort, sym = '$' }) {
  if (!stretch || !stretch.cost) return null;
  const autoCost = totalCost - stretch.cost;
  const autoEffort = totalEffort - stretch.hours;
  const deltaCost = stretch.cost;
  const deltaEffort = stretch.hours;
  const deltaPctCost = autoCost > 0 ? (deltaCost / autoCost) * 100 : 0;
  const fmt = (n) => `${sym}${Math.round(n).toLocaleString()}`;
  const stretching = stretch.planner_weeks > stretch.projected_weeks;
  const arrow = stretching ? '↑' : '↑';  // both add cost; visual cue is identical

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3"
      data-testid="timeline-stretch-comparison"
    >
      {/* Auto-projected */}
      <Card className="border-slate-200 bg-slate-50/40">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">
            Auto-projected ({stretch.projected_weeks} wk)
          </div>
          <div className="text-xl font-bold text-[#0F2040] tabular-nums" data-testid="ts-auto-cost">
            {fmt(autoCost)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {Math.round(autoEffort).toLocaleString()} hrs · what the math alone projects
          </div>
        </CardContent>
      </Card>

      {/* Planner-committed */}
      <Card className="border-cyan-200 bg-cyan-50/40">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-cyan-700 font-semibold mb-1">
            Planner-committed ({stretch.planner_weeks} wk)
          </div>
          <div className="text-xl font-bold text-[#0F2040] tabular-nums" data-testid="ts-planner-cost">
            {fmt(totalCost)}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {Math.round(totalEffort).toLocaleString()} hrs · with chosen timeline
          </div>
        </CardContent>
      </Card>

      {/* Delta */}
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
            Timeline Cost Delta
          </div>
          <div className="text-xl font-bold text-amber-700 tabular-nums" data-testid="ts-delta-cost">
            {arrow} +{fmt(deltaCost)}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            +{Math.round(deltaEffort).toLocaleString()} hrs · {deltaPctCost.toFixed(1)}% of auto-projected
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
